import "@tento-ui/component-builtin";
import "@tento-ui/components/icons/tasks-icon";

import { LonaWebComponent, html } from "@tento-ui/component";
import { Row } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TasksIcon } from "@tento-ui/components/icons/tasks-icon";
import { $makeNuxCtaRow } from "./cta-row";

const EXAMPLES = ["Buy groceries", "Call the dentist", "Finish the report"];

/**
 * Onboarding tip card: capturing tasks.
 *
 * Shows a few example task rows and an "Open tasks" CTA built from the shared
 * `$makeNuxCtaRow` helper (std-list-cell-layout + std-tasks-icon) — the same
 * row design as the scheduling card. CTA jumps to the Tasks section
 * (`onOpenTasks` → `focusSidebarSection("tasks")`).
 */
@component({ name: "std-nux-add-task-card" })
export class NuxAddTaskCard extends LonaWebComponent {
  private $examples: HTMLElement = this.$("examples");
  private $ctaAnchor: HTMLElement = this.$("cta-anchor");
  private onOpenTasks: Option<() => void> = null;

  static makeWith(callbacks: NuxAddTaskCard.Callbacks): NuxAddTaskCard {
    return NuxAddTaskCard.make().bind(callbacks);
  }

  bind(callbacks: NuxAddTaskCard.Callbacks): this {
    this.onOpenTasks = callbacks.onOpenTasks;

    this.$examples.replaceChildren();
    for (const label of EXAMPLES) {
      const $task = Row.make();
      $task.classList.add("task");

      const $box = document.createElement("span");
      $box.classList.add("checkbox");
      $task.appendChild($box);

      const $label = document.createElement("span");
      $label.classList.add("task-label");
      $label.textContent = label;
      $task.appendChild($label);

      this.$examples.appendChild($task);
    }

    this.$ctaAnchor.replaceChildren(
      $makeNuxCtaRow({
        $icon: TasksIcon.makeWith({ variant: "grayscale" }),
        label: "Open tasks",
        onClick: () => this.onOpenTasks?.(),
      }),
    );

    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        height: 100%;
      }

      #container {
        height: 100%;
        padding: 12px;
        justify-content: flex-end;
        gap: 12px;
      }

      #examples {
        gap: 2px;
      }

      .task {
        align-items: center;
        gap: 10px;
        padding: 6px 8px;
      }

      .checkbox {
        width: 16px;
        height: 16px;
        border-radius: 5px;
        flex-shrink: 0;
        box-shadow: inset 0 0 0 1.5px var(--text-ter);
      }

      .task-label {
        color: var(--text-sec);
        font-size: var(--text-12px);
      }
    `,
  ];

  static $html = html`
    <std-col id="container">
      <std-col id="examples"></std-col>
      <std-col id="cta-anchor"></std-col>
    </std-col>
  `;
}

export namespace NuxAddTaskCard {
  export type Callbacks = {
    onOpenTasks: () => void;
  };
}
